import { loadAllModules, loadEsriCss } from '../utils/esriLoader';
import React from 'react';
import ReactDOM from 'react-dom';
import {
  computeHistogramReferences,
  squaredMetersToAcres,
} from '../config/histogramDictionary';
import { addFeatures } from '@esri/arcgis-rest-feature-layer';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import {
  saveSnapshot,
  updatePrecalculatedCSV,
  toggleDrawShapePanel,
  toggleAnalysis,
  toggleReport,
  toggleLoader,
  updateHistogramResults,
  updateUserDrawnPolygon,
  setMapLayers,
  updateCharts,
  updateTotalAreaForCSV,
  toggleBasemapSelector,
  updatePrecalculatedShapeName,
} from '../reducers/mapview/actions';
import {
  createColumnCharts,
  createColumnAndSplineChart,
  createDonutChart,
  createHorizontalBarChart,
  calculatePlowprintArea,
  calculateIntactArea,
  calculatePlowprintExpansion,
} from '../utils/reportCharts';
import {
  reportCharts,
  emailSignupUrl,
  createSortOrder,
  getCSVKey,
  outfieldsToIgnore,
  getCSVCategory,
  getCSVYear,
} from '../config/staticText.config';
import { layers } from '../config/layers.config';
import store from '../store/store';
import * as Terraformer from 'terraformer-arcgis-parser';
import shp from 'shpjs';
import BasemapSelectorToggle from '../components/mapview/BasemapSelectorToggle';
import { text } from '../config/staticText.config';

let _esriModules = null;
let _map = null;
let _view = null;
let _layerList = null;
let _sketchVM = null;
let _graphicsLayerForSketchViewModel = null;

//Redux Actions / local function
let _setMapLayers = null;



export const emailRequest = (email) => {
  // https://community.esri.com/thread/77916
  // https://developers.arcgis.com/rest/services-reference/add-features.htm

  const attributes = [
    {
      attributes: {
        email: `${email}`,
      },
    },
  ];

  addFeatures({
    url: emailSignupUrl,
    features: attributes,
  })
    .then(() => {
      Swal.fire({
        title: 'Success!',
        text: `Successfully signed up with: ${email}`,
        type: 'success',
        confirmButtonText: 'Go To App',
      });
    })
    .catch(() => {
      Swal.fire({
        title: 'Error!',
        text: `Error signing up with ${email}`,
        type: 'error',
        confirmButtonText: 'Go To App',
      });
    });
};

const snapshotMapview = () => {
  var options = {
    width: 3000,
    height: 1000,
    format: 'png',
  };

  _view
    .takeScreenshot(options)
    .then((screenshot) => {
      store.dispatch(saveSnapshot(screenshot.dataUrl));
    })
    .catch((err) =>
      store.dispatch(toggleLoader({ active: false, loadingText: err }))
    );
};

export const setSketch = (SketchViewModel, GraphicsLayer) => {
  const { geometryEngineAsync } = _esriModules;
  const tempGL = new GraphicsLayer({
    id: 'sketchGraphics',
  });

  _sketchVM = new SketchViewModel({
    layer: tempGL,
    view: _view,
  });

  _sketchVM.polylineSymbol = {
    type: 'simple-line',
    color: 'red',
    width: 2,
  };

  _sketchVM.on('create', (event) => {
    if (event.state === 'complete') {
      _graphicsLayerForSketchViewModel.removeAll();
      _graphicsLayerForSketchViewModel.add(event.graphic);
      geometryEngineAsync
        .geodesicArea(event.graphic.geometry, 'acres')
        .then((res) => store.dispatch(updateTotalAreaForCSV(res)));
      store.dispatch(toggleReport(true));
      store.dispatch(toggleAnalysis(true));
      store.dispatch(updateUserDrawnPolygon(event.graphic.geometry));
    }
  });
};

export const removeGraphicsFromMap = () => {
  if (_graphicsLayerForSketchViewModel) {
    _graphicsLayerForSketchViewModel.removeAll();
  }
  if (_view && _view.graphics) {
    _view.graphics.removeAll();
  }
};

export const clearFileUpload = () => {
  if (document.getElementById('file-upload')) {
    document.getElementById('file-upload').value = null;
  }
};

export const drawPolygon = () => {
  // Clear all of the existing data when a user starts drawing a new shape
  removeGraphicsFromMap();
  clearFileUpload();
  store.dispatch(updateTotalAreaForCSV(null));
  store.dispatch(updateCharts({}));
  store.dispatch(updatePrecalculatedShapeName(null));
  store.dispatch(updatePrecalculatedCSV(''));
  _sketchVM.create('polygon', { mode: 'click' });
};

export const updateDefinitionExpression = (year, layerID) => {
  const layer = getLayerByID(layerID);
  layer.definitionExpression = `Year = ${year}`;
};

export const loadEsriResources = async () => {
  const esriImports = [
    'esri/Map',
    'esri/WebMap',
    'esri/views/MapView',
    'esri/widgets/Legend',
    'esri/layers/Layer',
    'esri/layers/ImageryLayer',
    'esri/layers/FeatureLayer',
    'esri/layers/MapImageLayer',
    'esri/layers/VectorTileLayer',
    'esri/layers/WebTileLayer',
    'esri/widgets/Search',
    'esri/Basemap',
    'esri/WebMap',
    'esri/widgets/LayerList',
    'esri/widgets/ScaleBar',
    'esri/layers/GraphicsLayer',
    'esri/widgets/Sketch/SketchViewModel',
    'esri/tasks/support/Query',
    'esri/tasks/QueryTask',
    'esri/request',
    'esri/widgets/BasemapGallery',
    'esri/Graphic',
    'esri/layers/TileLayer',
    'esri/geometry/geometryEngineAsync',
    'esri/layers/support/MosaicRule',
    'esri/core/watchUtils',
    'esri/tasks/Locator',
  ];
  _esriModules = await loadAllModules(esriImports);
  loadEsriCss();
};

export const removeGeometry = () => {
  store.dispatch(updateUserDrawnPolygon(null));
  store.dispatch(toggleReport(false));
  store.dispatch(updateCharts({}));
  removeGraphicsFromMap();
  clearFileUpload();
};

export const fileUpload = (e) => {
  const { Graphic, geometryEngineAsync } = _esriModules;

  const reader = new FileReader();

  reader.onload = (result) => {
    try {
      removeGraphicsFromMap();
      const zip = shp.parseZip(reader.result);
      const polygon = Terraformer.convert(zip.features[0].geometry);

      const polyGraphic = {
        type: 'polygon',
        rings: polygon.rings,
      };

      const fillSymbol = {
        type: 'simple-fill',
        outline: {
          color: [255, 255, 255],
          width: 1,
        },
      };

      const polygonGraphic = new Graphic({
        geometry: polyGraphic,
        symbol: fillSymbol,
      });

      _view.graphics.add(polygonGraphic);

      store.dispatch(updateUserDrawnPolygon(polygonGraphic.geometry));
      geometryEngineAsync
        .geodesicArea(polygonGraphic.geometry, 'acres')
        .then((res) => store.dispatch(updateTotalAreaForCSV(res)));
      store.dispatch(toggleReport(true));
    } catch {
      console.error('Error reading file.');
      Swal.fire({
        title: 'Error Uploading Shapefile',
        text: text.errorFileUpload,
        type: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  reader.onerror = (error) => {
    console.error('Error reading file: ', error);
  };

  reader.readAsArrayBuffer(e.target.files[0]);
};

export const runHistogram = (startYear, endYear) => {
  console.log('generating report...');
  const { request, MosaicRule, watchUtils } = _esriModules;
  const polygonGeometry = store.getState().mapview.userDrawnPolygon;

  // do not run the analysis if no area provided
  if (polygonGeometry.__accessor__ === undefined) {
    Swal.fire({
      title: 'No Geometry provided',
      text: text.errorNoGeometry,
      type: 'error',
      confirmButtonText: 'OK',
    });
    return;
  }

  store.dispatch(
    toggleLoader({ active: true, loadingText: 'Calculating Plowprint Report' })
  );

  _view
    .goTo(polygonGeometry)
    .then(() => {
      watchUtils.whenFalseOnce(_view, 'updating', () => {
        snapshotMapview();
      });
    })
    .catch(() =>
      store.dispatch(
        toggleLoader({ active: false, loadingText: 'Updating Mapview' })
      )
    );

  const arrayOfRequests = computeHistogramReferences.map((service) => {
    if (service.mosaicRule) {
      var mosaicRule = new MosaicRule({ where: service.mosaicRule });
    }

    return request(service.url, {
      responseType: 'json',
      query: {
        f: 'json',
        geometryType: 'esriGeometryPolygon',
        geometry: polygonGeometry,
        mosaicRule: mosaicRule,
      },
    });
  });

  const histogramResults = [];
  Promise.all(arrayOfRequests)
    .then((res) => {
      store.dispatch(
        toggleLoader({
          active: true,
          loadingText: 'Calculating Plowprint Report',
        })
      );

      res.forEach((result, resultIndex) => {
        computeHistogramReferences[resultIndex].subCategories.forEach(
          (subCategory) => {
            const [category, countIndex] = subCategory;
            const { years, squareMetersPerPixel, id } =
              computeHistogramReferences[resultIndex];

            years.forEach((year, yearIndex) => {
              const columnDPixels =
                result.data.histograms[yearIndex].counts[countIndex] || 0;
              const pixelsToSquareMeters = columnDPixels * squareMetersPerPixel;
              const rowOfHistogramData = {
                dataCategory: id,
                dataSubcategory: category,
                dataYear: year,
                pixelCount: columnDPixels,
                acreCount: Math.round(
                  pixelsToSquareMeters * squaredMetersToAcres
                ),
              };

              histogramResults.push(rowOfHistogramData);
            });
          }
        );
      });
      const filteredByYear = histogramResults.filter(
        (data) =>
          (data.dataYear >= startYear && data.dataYear <= endYear) ||
          data.dataYear === 'N/A'
      );
      createColumnCharts(
        reportCharts.plowprintExpansion,
        filteredByYear,
        startYear,
        endYear
      );

      createColumnAndSplineChart(
        reportCharts.plowprintCompositionAndOwnership,
        filteredByYear,
        startYear,
        endYear
      );
      createColumnAndSplineChart(
        reportCharts.intactCompositionAndOwnership,
        filteredByYear,
        startYear,
        endYear
      );
      createDonutChart(reportCharts.openWater, filteredByYear, endYear);
      createHorizontalBarChart(reportCharts.soilQuality, filteredByYear, true);
      calculatePlowprintArea(filteredByYear, endYear);
      calculateIntactArea(filteredByYear, endYear);
      calculatePlowprintExpansion(filteredByYear, startYear, endYear);
      store.dispatch(updateHistogramResults(filteredByYear));
      store.dispatch(toggleLoader({ active: false, loadingText: '' }));
    })
    .catch((err) => {
      // console.log('The polygon geometry :: ', polygonGeometry);
      console.log(text.errorLargeGeometryLog, err);
      Swal.fire({
        title: 'Error Calculating Plowprint Report',
        text: text.errorLargeGeometry,
        type: 'error',
        confirmButtonText: 'OK',
      });
      store.dispatch(toggleLoader({ active: false, loadingText: '' }));
    });
};

const sortOutfields = (a, b, sortOrder) => {
  const aa = `${a[0]} ${a[1]} ${a[2]}`;
  const bb = `${b[0]} ${b[1]} ${b[2]}`;
  return sortOrder[aa] - sortOrder[bb];
};

const addPlaceholderData = (arrayOfIncompleteData) => {
  /*
   * In order to properly create charts, our Highcharts data must contain an equal number of values for each category and year.
   * We can't guarentee the services have this data, so we need to check each category to ensure that all of its subcategories have data for each year.
   * If there isn't any data, we plug a 0 value.
   */

  const completeDataSet = [...arrayOfIncompleteData];

  const setOfDataCategories = new Set(); // Isolate each dataCategory so we can iterate over the subCategories specific to that dataCategory.

  arrayOfIncompleteData.forEach(({ dataCategory }) =>
    setOfDataCategories.add(dataCategory)
  );

  setOfDataCategories.forEach((category) => {
    const categoryData = arrayOfIncompleteData.filter(
      ({ dataCategory }) => dataCategory === category
    );

    const setOfSubCategories = new Set(
      categoryData.map(({ dataSubcategory }) => dataSubcategory)
    ); // Isolate the subCategories for this specific data category

    const dataYears = categoryData
      .filter(({ dataYear }) => !isNaN(dataYear)) // Insure that we ignore data that doesn't correspond with yearly data
      .map((cat) => cat.dataYear);

    const maxYear = Math.max(...dataYears);
    const minYear = Math.min(...dataYears);

    for (let x = minYear; x < maxYear; x++) {
      // Check if a subCategory has data in each year. Plug those that don't.
      setOfSubCategories.forEach((subCategory) => {
        const subCategoryYearData = categoryData.find(
          ({ dataSubcategory, dataYear }) =>
            dataSubcategory === subCategory && dataYear === x
        );

        if (!subCategoryYearData) {
          completeDataSet.push({
            acreCount: 0,
            dataCategory: category,
            dataSubcategory: subCategory,
            dataYear: x,
          });
        }
      });
    }
  });

  return completeDataSet;
};

/**
 * Init function that takes in container dom reference and loads map, mapview, click handlers and default layers.
 * @param {HTMLElement} domRef DOM Reference passed from React
 */
export const initializeMap = (domRef) => {
  const {
    ImageryLayer,
    MapImageLayer,
    VectorTileLayer,
    Query,
    QueryTask,
    Basemap,
    BasemapGallery,
    MapView,
    Search,
    ScaleBar,
    LayerList,
    SketchViewModel,
    GraphicsLayer,
    Map,
  } = _esriModules;

  const map = new Map({
    basemap: 'hybrid',
  });

  /************************************************************
   * Set the WebMap instance to the map property in a MapView.
   ************************************************************/

  _view = new MapView({
    // map: _map,
    map: map,
    container: domRef,
    constraints: {
      rotationEnabled: false,
      maxZoom: 13,
    },
    popup: {
      dockOptions: {
        buttonEnabled: false,
      },
    },
    zoom: 5,
    center: [-98.11601679140786, 40.79163179608149],
  });

  _map = _view.map;
  const scaleBar = new ScaleBar({
    view: _view,
    unit: 'dual',
  });

  const search = new Search({
    view: _view,
  });

  // Add the basemap toggler to the default UI so it plays nicely with the zoom and other ui components.
  const toggleFunction = () =>
    store.dispatch(
      toggleBasemapSelector(!store.getState().mapview.showBasemapSelector)
    );
  const node = document.createElement('div');
  ReactDOM.render(
    <BasemapSelectorToggle toggleFunction={toggleFunction} />,
    node
  );

  // If the device is a mobile device or ipad (1025px), load the application with the ui in the top left to optimize space.
  _view.ui.add(search, { position: 'top-right' });
  if (window.innerWidth < 960) {
    _view.ui.remove('zoom');
    _view.ui.add(node, { position: 'top-left' });
    _view.ui.add(scaleBar, { position: 'top-left' });
  } else {
    _view.ui.move('zoom', { position: 'bottom-right' });
    _view.ui.add(node, { position: 'bottom-right' });
    _view.ui.add(scaleBar, { position: 'bottom-right' });
  }

  const topo = Basemap.fromId('topo');
  const osm = Basemap.fromId('osm');
  const hybrid = Basemap.fromId('hybrid');

  const darkGrayBasemap = new Basemap({
    baseLayers: [
      new VectorTileLayer({
        portalItem: {
          id: '5e9b3685f4c24d8781073dd928ebda50',
        },
      }),
    ],
    referenceLayers: [
      new VectorTileLayer({
        portalItem: {
          id: '747cb7a5329c478cbe6981076cc879c5',
        },
      }),
    ],
    thumbnailUrl:
      'https://www.arcgis.com/sharing/rest/content/items/7742cd5abef8497288dc81426266df9b/info/thumbnail/thumbnail1607387673856.jpeg',
    title: 'Dark Gray',
  });
  const terrainBasemap = new Basemap({
    baseLayers: [
      new VectorTileLayer({
        portalItem: {
          id: '33064a20de0c48d2bb61efa8faca93a8',
        },
      }),
      new MapImageLayer({
        portalItem: {
          id: '1b243539f4514b6ba35e7d995890db1d',
        },
      }),
    ],
    referenceLayers: [
      new VectorTileLayer({
        portalItem: {
          id: '14fbc125ccc9488888b014db09f35f67',
        },
      }),
    ],
    thumbnailUrl:
      'https://www.arcgis.com/sharing/rest/content/items/33064a20de0c48d2bb61efa8faca93a8/info/thumbnail/thumbnail1561651213545.jpeg',
    title: 'Hillshade',
  });

  hybrid.title = 'Imagery'; // Override default layer title
  const basemapCollection = [
    topo,
    hybrid,
    osm,
    darkGrayBasemap,
    terrainBasemap,
  ];

  _map.basemapGallery = new BasemapGallery({
    view: _view,
    container: 'basemap-div',
    source: basemapCollection,
  });

  window._map = _map;
  window._view = _view;

  _view
    .when(() => {
      // console.log('_view is loaded');
      //add layers
      _setMapLayers(getLayerIDs());
      _graphicsLayerForSketchViewModel = new GraphicsLayer();
      _map.add(_graphicsLayerForSketchViewModel);

      setSketch(SketchViewModel, GraphicsLayer);
    })
    .catch((error) => console.error(error));

  _layerList = new LayerList({
    view: _view,
  });

  _view.popup.watch('selectedFeature', (selectedFeature) => {
    if (
      selectedFeature &&
      selectedFeature.sourceLayer &&
      selectedFeature.sourceLayer.label &&
      selectedFeature.sourceLayer.label.includes('Counties')
    ) {
      if (_view.graphics) {
        _view.graphics.removeAll();
      }

      selectedFeature.symbol = {
        type: 'simple-fill',
        color: [150, 150, 150, 0.2],
        outline: {
          color: [50, 50, 50, 1],
          width: 1,
        },
      };
      _view.graphics.add(selectedFeature);
    }
  });

  _view.popup.watch('visible', (visibility) => {
    if (!visibility && _view.graphics) {
      _view.graphics.removeAll();
    }
  });

  _view.popup.viewModel.on('trigger-action', (event) => {
    if (event.action.id === 'run-analysis') {
      store.dispatch(
        toggleLoader({
          active: true,
          loadingText: 'Calculating Plowprint Report',
        })
      );
      store.dispatch(toggleAnalysis(true));
      removeGeometry();
      store.dispatch(updateTotalAreaForCSV(null));
      snapshotMapview();
      const currentFeature = event.target.selectedFeature;
      const featureSublayerId = currentFeature.sourceLayer.id;
      const mapImageLayer = layers.find((layer) => layer.id === 'topLevel')
        .options.url;

      const mapserviceUrl = `${mapImageLayer}/${featureSublayerId}`;
      const queryObject = new Query();
      const queryTask = new QueryTask({ url: mapserviceUrl });

      queryObject.outFields = ['*'];
      queryObject.returnGeometry = false;

      // Each sublayer has uniqure outfields that contain the information we want to display, so we use a switch statement to set the where clause accordingly.
      let popupName;
      switch (featureSublayerId) {
        case 0:
          queryObject.where = `Area_Num = ${currentFeature.attributes.Area_Num}`;
          popupName = currentFeature.attributes.area_names;
          break;

        case 1:
          queryObject.where = '1 = 1';
          popupName = 'Northern Great Plains';
          break;

        case 2:
          queryObject.where = '1 = 1';
          popupName = 'Southern Great Plains';
          break;

        case 3:
          queryObject.where = '1 = 1';
          popupName = 'CEC Great Plains';
          break;

        case 4:
          queryObject.where = `JV = '${currentFeature.attributes.JV}'`;
          popupName = `Joint Venture ${currentFeature.attributes.JV}`;
          break;

        case 5:
          queryObject.where = `OBJECTID = ${currentFeature.attributes.OBJECTID}`;
          popupName = `${currentFeature.attributes.County}, ${currentFeature.attributes.STATE_NAME}`;
          break;

        case 6:
          queryObject.where = `Name_1 = '${currentFeature.attributes.Name_1}'`;
          popupName = `State or Province: ${currentFeature.attributes.Name_1}`;
          break;

        default:
          break;
      }

      queryTask
        .execute(queryObject)
        .then((res) => {
          if (res.features.length === 0) {
            Swal.fire({
              title: 'Error processing histogram data.',
              type: 'error',
              confirmButtonText: 'OK',
            });
            console.log('Query returned no features.');
          } else {
            const feature = res.features[0].attributes;
            const filteredFeatures = Object.keys(feature).filter(
              (key) => !outfieldsToIgnore.includes(key)
            );

            store.dispatch(updatePrecalculatedShapeName(popupName));
            const totalFeatureArea = Math.round(
              feature.Shape_Area * squaredMetersToAcres
            );

            const csvDataName = [];
            const csvCategories = [];
            const csvYears = [];

            filteredFeatures.forEach((key) => {
              csvDataName.push(getCSVKey(key));
              csvCategories.push(getCSVCategory(key));
              csvYears.push(getCSVYear(key));
            });

            const startYear = store.getState().mapview.yearsSelected.start;
            const endYear = store.getState().mapview.yearsSelected.end;

            // For each index, filter by year, pull the csv row name from dictionary and recalc pixels to acres.
            const csvAcreCounts = [];
            filteredFeatures.map((key) => {
              const valueConvertedToAcres = Math.round(
                feature[key] * squaredMetersToAcres
              );
              csvAcreCounts.push(valueConvertedToAcres);
            });

            let csvDataString = 'data:text/csv;charset=utf-8,';
            csvDataString += 'Data, Category, Year, Area Count (Acres) \n';
            csvDataString += `Total Area, N/A, N/A, ${totalFeatureArea} \n`;

            const pdfData = [];
            const csvDataArray = [];
            for (let i = 0; i < csvDataName.length; i++) {
              csvDataArray.push([
                csvDataName[i],
                csvCategories[i],
                csvYears[i],
                csvAcreCounts[i],
              ]);
              const convertedDataYear = isNaN(Number(csvYears[i]))
                ? csvYears[i]
                : Number(csvYears[i]);
              pdfData.push({
                acreCount: csvAcreCounts[i],
                dataCategory: csvDataName[i],
                dataSubcategory: csvCategories[i],
                dataYear: convertedDataYear,
              });
            }

            const csvDataFilteredByYear = csvDataArray.filter(
              (data) =>
                (data[2] >= startYear && data[2] <= endYear) ||
                data[2] === 'N/A'
            );

            const sortOrder = createSortOrder();

            const sortedCSVData = csvDataFilteredByYear.sort((a, b) =>
              sortOutfields(a, b, sortOrder)
            );

            sortedCSVData.forEach((item) => {
              csvDataString += `${item[0]},${item[1]},${item[2]}, ${item[3]} \n`;
            });
            store.dispatch(updatePrecalculatedCSV(csvDataString));

            const pdfDataFilteredByYear = pdfData.filter(
              (data) =>
                (data.dataYear >= startYear && data.dataYear <= endYear) ||
                data.dataYear === 'N/A'
            );

            const completeDataSet = addPlaceholderData(pdfDataFilteredByYear);

            createColumnCharts(
              reportCharts.plowprintExpansion,
              completeDataSet,
              startYear,
              endYear
            );
            createColumnAndSplineChart(
              reportCharts.plowprintCompositionAndOwnership,
              completeDataSet,
              startYear,
              endYear
            );
            createColumnAndSplineChart(
              reportCharts.intactCompositionAndOwnership,
              completeDataSet,
              startYear,
              endYear
            );
            createDonutChart(reportCharts.openWater, completeDataSet, endYear);
            createHorizontalBarChart(
              reportCharts.soilQuality,
              completeDataSet,
              false
            );
            calculatePlowprintExpansion(completeDataSet, startYear, endYear);
            calculatePlowprintArea(completeDataSet, endYear)
              .then(() => {
                calculateIntactArea(completeDataSet, endYear);
              })
              .then(() =>
                store.dispatch(updateTotalAreaForCSV(totalFeatureArea))
              );

            store.dispatch(updateHistogramResults(completeDataSet));
            store.dispatch(toggleDrawShapePanel(true));
            snapshotMapview();
          }
          store.dispatch(toggleLoader({ active: false, loadingText: '' }));
        })
        .catch((err) => {
          console.log('error: ', err);
          store.dispatch(toggleLoader({ active: false, loadingText: '' }));
        });
    }
  });

  _layerList = new LayerList({
    view: _view,
  });

  const rasterLayers = [];
  const mapImageLayers = [];

  layers.forEach((l) => {
    let rasterLayer;
    let mapImgLayer;

    switch (l.type) {
      case 'ImageryLayer':
        rasterLayer = new ImageryLayer(l.options);
        break;

      case 'MapImageLayer':
        mapImgLayer = new MapImageLayer(l.options);
        break;

      default:
        break;
    }

    if (rasterLayer) {
      rasterLayers.push(rasterLayer);
    }
    if (mapImgLayer) {
      mapImageLayers.push(mapImgLayer);
    }
  });

  rasterLayers.forEach((layer) => _view.map.add(layer));
  mapImageLayers.forEach((layer) => _view.map.add(layer));
};

export const getLayerIDs = () => {
  const layerIDs = [];
  _map.layers.items.forEach((layer) => {
    if (layer.id !== 'topLevel') {
      layerIDs.push(layer.id);
    }
  });
  layerIDs.unshift('topLevel');
  return layerIDs;
};

export const getLayerByID = (id) => {
  return _map.findLayerById(id);
};

export const updateLayerFromLayerController = (key, value, id) => {
  const layerToUpdate = _map.findLayerById(id);
  if (layerToUpdate) {
    layerToUpdate[key] = value;
  }
};

export const updateSublayerFromLayerController = (
  layerProperty,
  value,
  sublayerID,
  parentLayerID
) => {
  const parentLayer = _map.findLayerById(parentLayerID);
  if (parentLayer) {
    const sublayerToUpdate = parentLayer.findSublayerById(sublayerID);
    if (sublayerToUpdate) {
      sublayerToUpdate[layerProperty] = value;
    }
  }
};

export const getLayerSymbology = async (id) => {
  const layer = _map.findLayerById(id);
  const { request } = _esriModules;
  if (layer) {
    switch (layer.type) {
      case 'imagery':
        if (layer.rasterFunctionInfos) {
          const rasterInfoForLegend = layer.rasterFunctionInfos.find(
            (rasterInfo) => {
              return rasterInfo.name !== '' || rasterInfo.name !== 'None';
            }
          );
          const name = rasterInfoForLegend.name;
          let legendURL = `${layer.url}/legend?bandIds=&renderingRule=${name}&f=json`;
          if (layer.title === 'Developed Area') {
            // The developed Area comes from the water service, so we need to add a raster function in order to get its symbology.
            legendURL =
              'https://maps.wwfus.org/server/rest/services/Plowprint/waterdev/ImageServer/legend?bandIds=&renderingRule=%7B%22rasterFunction%22%3A%22symbology_waterdev_developed%22%7D&f=json';
          }
          const rasterLegendResponse = await request(legendURL, {
            responseType: 'json',
          });

          return {
            type: layer.type,
            title: layer.title,
            legend: rasterLegendResponse.data.layers[0].legend,
          };
        }
        return 'hide';

      case 'map-image':
        if (layer.sublayers.items.length > 1) {
          const arrayOfSublayerReq = layer.sublayers.items.map((sublayer) => {
            /* Loop through sublayers, get the id, use the id to build the url
            then use esri request to get the drawing info */
            const sublayerID = sublayer.id;
            const sublayerLegendURL = `${layer.url}/${sublayerID}?f=pjson`;
            return request(sublayerLegendURL, {
              responseType: 'json',
            });
          });
          const mapImagePromiseRes = Promise.all(arrayOfSublayerReq)
            .then((res) => {
              const legendInfo = res.map((sublayerRes, index) => {
                const sublayer = layer.sublayers.items[index];
                const rendererType = sublayerRes.data.drawingInfo.renderer.type;
                let color = '#FFFFFF';
                if (rendererType === 'simple') {
                  const symbolPath =
                    sublayerRes.data.drawingInfo.renderer.symbol;
                  const reducer = (subtotal, currentValue) =>
                    subtotal + currentValue;
                  /* Add up the rbga numbers in the array for color value.
                If it's zero, that means we need to save the outline color instead. */
                  const colorArr =
                    symbolPath.color.reduce(reducer) > 0
                      ? symbolPath.color
                      : symbolPath.outline.color;
                  color = `rgba(${colorArr})`;
                }
                const sLLabel = layer.sublayers.items.find((sL) => {
                  return sL.id === sublayerRes.data.id;
                });
                const label = sLLabel.label;

                return {
                  sublayerID: sublayer.id,
                  label,
                  color,
                };
              });
              return legendInfo;
            })
            .catch((err) => {
              console.log('Error processing request: ', err);
            });
          return mapImagePromiseRes;
        }
        return null;

      case 'graphics':
        return null;

      default:
        return null;
    }
  }
};

_setMapLayers = (mapLayers) => {
  store.dispatch(setMapLayers(mapLayers));
};
